import { Link } from "gatsby"
import React, { useContext } from "react"
import Layout from "../../components/layout"
import Seo from "../../components/seo"
import { StaticImage } from "gatsby-plugin-image"

import styled from "styled-components"

import WebriQForm from "@webriq/gatsby-webriq-form"

const Banner = () => {
  return (
    <>
      <div className="hero-new-study">
        <div className="container">
          <div className="row">
            <div className="col-md-12 mx-auto">
              <div className="text-center title-banner">
                <h1>
                  Field Service Firm Builds ERP on Quickbase, Saving 300+ Hours
                  By Automating Invoicing, HR, and Asset Management
                </h1>
                <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-sm-start text-center text-sm-left text-white">
                  <p className="pr-sm-1 mb-1 mb-sm-0">
                    <strong>Summary:</strong>
                  </p>
                  <p>
                    Manual invoicing and job tracking took the Advanced Fiber
                    Inc (AFI) executive team four days a week. Quandary replaced
                    those processes with a custom Quickbase-based ERP, saving
                    over 300 hours and giving the business a scalable system
                    tailored to field operations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  padding: 24px 0;
`

const LogoCard = styled.div`
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  min-width: 160px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 12px;
`

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 48px;
  object-fit: contain;
`

const AFI = ({ location }) => {
  return (
    <>
      <Layout banner={<Banner />} location={location}>
        <Seo
          title="Custom ERP Saves 300+ Hours for Field Service Firm"
          description="Quandary helped AFI cut 300+ hours of manual work with a custom ERP that automated invoicing, centralized ops, and scaled with the business."
        />
        <div className="py-4 py-md-5 case-studies-inner new-case-studies-inner bg-white">
          <div className="container">
            <div className="row">
              <div className="col-md-4 col-lg-4">
                <div
                  className="sidebar-tab relative results"
                  style={{ position: "sticky", top: "110px" }}
                >
                  <div className="relative results bg-case-study p-4">
                    <h3 className="mt-0 ">Results Snapshot</h3>
                    <ul className="ml-0">
                      <li>300+ hours saved annually</li>
                      <li>Invoicing time cut from days to minutes</li>
                      <li>Single system replaced multiple tools</li>
                      <li>Improved billing accuracy across field jobs</li>
                      <li>Scalable ERP tailored to field operations</li>
                    </ul>
                  </div>
                  <a
                    className="btn btn-primary btn-arrow mr-2 mt-4 text-center relative w-full"
                    download
                    target="_blank"
                    href="https://cdn.sanity.io/files/55lcecww/production/d05ff2448972746102e7e96cb9472d267cd5f330.pdf"
                  >
                    Download This Story
                  </a>
                </div>
              </div>
              <div className="col-md-8 col-lg-8">
                <section className="relative">
                  <div className="relative px-0">
                    <div className="row mx-0">
                      <div className="col-md-11 mx-auto p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Challenge</h2>
                          <p>
                            AFI’s operations were growing while their systems
                            struggled to scale. The team relied on a simple {""}
                            <Link to="/partners/quickbase/">Quickbase</Link> app
                            to track fieldwork. However, it only captured
                            limited data and couldn’t support the complexity of
                            their billing and reporting needs.
                          </p>
                          <p>
                            The owner, Bob Rizzuto, spent 20+ hours a week
                            reviewing reports, creating invoices by hand, and
                            re-entering data into QuickBooks to reconcile the
                            books. On top of that, incoming work requests
                            arrived in CSV format, requiring weekly manual entry
                            just to get jobs into the queue.
                          </p>
                          <p>
                            The team didn’t have the bandwidth to rebuild the
                            system while keeping day-to-day operations running.
                            They needed to eliminate manual processes, reduce
                            the margin for error, and free up time to focus on
                            growth.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Our Solution</h2>
                          <p>
                            AFI initially sought out{" "}
                            <Link to="/">Quandary Consulting Group</Link> to
                            improve reporting and automate invoicing workflows.
                            After a detailed discovery, we built a custom
                            invoicing and reporting solution to automate several
                            manual processes.
                          </p>
                          <p>
                            From there, we iterated with the team on their
                            existing workflows, tech stack, and vision to expand
                            the platform into a custom ERP with CRM
                            functionality that helped them better service
                            customers.
                          </p>
                          <p>
                            The ERP integrated with Quickbooks, Samsara, and{" "}
                            <Link to="/peaksuite/">
                              PeakSuite Document Generator
                            </Link>{" "}
                            to improve operations, and an MS Outlook integration
                            provided the CRM component by automating internal
                            and external communications.
                          </p>
                          <p>
                            This gave AFI visibility into how work moved across
                            the business and confidence that nothing would slip
                            through the cracks.
                          </p>

                          <p>
                            <strong>Key Features</strong>
                          </p>
                          <p>
                            The project has evolved from a simple invoice
                            automation tool to a custom ERP and CRM fit to the
                            needs of this specific field service organization.
                          </p>
                          <ul className="pl-4">
                            <li>
                              <strong>Streamlined Invoicing: </strong> Generates
                              the invoice, sends it to the client, and
                              automatically updates Quickbooks (including
                              regenerated invoices).
                            </li>
                            <li>
                              <strong>Synchronized Client Requests:</strong>{" "}
                              Drag and drop input system for new client requests
                              with automatic job number assignments that match
                              PO numbers for tracking and billing.
                            </li>
                            <li>
                              <strong>Job Number Creation:</strong> Creates new
                              job numbers for jobs without numbers and matches
                              those jobs to PO numbers for easy billing.
                            </li>
                            <li>
                              <strong>HR Documentation: </strong> DocuSign
                              integration automates employee onboarding, safety
                              certification processes, and benefit distribution.
                            </li>
                            <li>
                              <strong>
                                Inventory Tracking and Management:{" "}
                              </strong>{" "}
                              Identifies, tracks, and manages materials in the
                              warehouse and costs for accurate billing and
                              reporting.
                            </li>
                            <li>
                              <strong>Scheduling: </strong> Manages on-call
                              status with automated scheduling in Quickbase with
                              a rotating schedule to prevent overworking team
                              members.
                            </li>
                            <li>
                              <strong>Improved Data Integrity:</strong>{" "}
                              Eliminating data entry errors from manual work,
                              improving the team’s insights, billing, and
                              reporting.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>Our Process</h2>
                          <p>
                            AFI’s operations team was hands-on from day one.
                            They came prepared with a clear plan, documentation,
                            and a structured base, allowing us to move fast and
                            iterate collaboratively.
                          </p>
                          <p>
                            We started with reporting and invoicing workflows,
                            then expanded the build into a broader ERP. Each
                            phase focused on solving real bottlenecks while
                            keeping operations running.
                          </p>
                          <p>
                            <strong>Key Steps:</strong>
                          </p>
                          <ol className="decimal">
                            <li>
                              <strong>Stabilized invoicing inputs:</strong>{" "}
                              Translated flat spreadsheet data into a relational
                              Quickbase structure that could support reporting,
                              scheduling, and billing
                            </li>
                            <li>
                              <strong>
                                Implemented foundational best practices:{" "}
                              </strong>{" "}
                              Corrected gaps in the original build and
                              established required fields, relational links, and
                              validation logic
                            </li>
                            <li>
                              <strong>Extended the reporting layer: </strong>{" "}
                              Built a reliable weekly production report that
                              became the trigger for invoicing and field team
                              workflows
                            </li>
                            <li>
                              <strong>
                                Created a job request import system:{" "}
                              </strong>{" "}
                              Converted weekly CSVs into structured data, with
                              automatic job and PO number matching
                            </li>
                            <li>
                              <strong>
                                Established a continuous improvement loop:{" "}
                              </strong>{" "}
                              Met regularly with the client to identify manual
                              tasks, evaluate feasibility, and roll out targeted
                              automation projects
                            </li>
                          </ol>
                          <p>
                            This approach allowed AFI to scale systems without
                            pausing operations or launching a full rebuild.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-gradient">
                        <div className="client-content">
                          <h2 className="text-white">The Roadblocks</h2>
                          <p className="text-white">
                            The team managed processes with paper and
                            spreadsheets. Transitioning from unstructured flat
                            data to structured, organized (relational) data was
                            an adjustment. For a smooth transition, we analyzed
                            their business, processes, and data in depth before
                            breaking it out into the relational database
                            structure.
                          </p>
                          <p className="text-white">
                            Several processes were not standardized. To support
                            nuance while standardizing the general user process,
                            we set up guardrails automations. Then, we evaluated
                            exceptions on the back end and mapped out
                            automations for those instances. Users get a
                            standard front-end experience while we update the
                            back-end to support custom requests.
                          </p>
                          <p className="text-white">
                            Recently, the client’s invoicing workflow grew
                            beyond Quickbase's capabilities. Relying on our
                            full-stack developers, {""}
                            <Link to="/approach">we quickly pivoted</Link>
                            {""} the client to a custom solution built on AWS
                            that supported that unique business case. We also
                            coached the client to prevent a drastic migration
                            from the platform, given that it still provided
                            significant value to the business.
                          </p>
                          <p className="text-white">
                            As a field service team, they needed significant
                            mobile functionality for their tools to prevent
                            repeat work. While Quickbase has mobile
                            functionality built into the app, we needed to
                            extend that functionality to support multi-line,
                            multi-record structures and support app
                            functionality while working offline to ensure
                            adoption.
                          </p>
                          <blockquote
                            className="testimonial"
                            style={{
                              background: "#fff",
                              borderRadius: "24px",
                              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                              padding: "32px 24px 16px 24px",
                              maxWidth: "100%",
                              textAlign: "center",
                              position: "relative",
                            }}
                          >
                            <div className="row mb-3">
                              <div className="col-lg-8">
                                <p
                                  style={{
                                    color: "#3a3a3a",
                                    fontStyle: "italic",
                                    fontSize: "20px",
                                  }}
                                >
                                  "It would be impossible for us to run our
                                  company without this system. We came to you
                                  because we were relying on Excel spreadsheets
                                  to manage everything, and there’s no way we
                                  could have grown to where we are now without
                                  it. "
                                </p>
                              </div>
                              <div className="col-lg-4 mt-4 mt-lg-0">
                                <div className="row">
                                  <div className="col-6 col-md-12">
                                    <div
                                      className="relative user-img mb-2"
                                      style={{
                                        backgroundImage:
                                          'url("https://cdn.sanity.io/images/55lcecww/production/56ccd6002fe6b279fa4421266a8893aaa69aad13-200x200.jpg")',
                                      }}
                                    />
                                  </div>
                                  <div className="col-6 col-md-12">
                                    <div className="relative text-left user-info ml-2">
                                      <h3>Bob Rizzuto</h3>
                                      <p className="my-2 my-md-0">
                                        President Advanced Fiber
                                      </p>
                                      <a
                                        href="https://www.linkedin.com/in/bob-rizzuto-b7076135/"
                                        target="_blank"
                                      >
                                        <svg
                                          fill="#00b5f4 "
                                          stroke-width="0"
                                          viewBox="0 0 448 512"
                                          height="2em"
                                          width="2em"
                                          xmlns="http://www.w3.org/2000/svg"
                                          style={{
                                            height: "2em; width: 1.2em",
                                          }}
                                        >
                                          <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
                                        </svg>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </blockquote>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Toolstack</h2>
                          <LogoGrid>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/78673169bfad856d32e060c5bf029890db184c60-261x101.png"
                                alt="Tool 1"
                              />
                            </LogoCard>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/1716d37ece5d020b05de6ab69b82bb2b4871a9bc-261x101.png"
                                alt="Tool 2"
                              />
                            </LogoCard>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/70140f70b33260432df377cf90bdf32921d11677-300x157.jpg"
                                alt="Tool 3"
                              />
                            </LogoCard>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/d9e1b1f439f1ab779a9fb3ab48074f6bd0720e02-400x110.png"
                                alt="Tool 4"
                              />
                            </LogoCard>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/d71f97547430407d3a68e913fc4994361cf9543d-360x246.png"
                                alt="Tool 5"
                              />
                            </LogoCard>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/53cd1156110ae7073888088451fdcec00193a279-443x114.png"
                                alt="Tool 6"
                              />
                            </LogoCard>
                          </LogoGrid>
                          <p>
                            AFI’s custom ERP relies on a focused toolset that
                            keeps operations lean and responsive:
                          </p>
                          <ul className="pl-4">
                            <li>
                              <strong>Samsara Integration:</strong> Automates
                              vehicle maintenance and safety tracking and
                              reports for the entire fleet.
                            </li>
                            <li>
                              <Link to="/quickbase-integration-services/">
                                Quickbooks Integration:{" "}
                              </Link>
                              {""} Automates invoicing and accounting
                              management.
                            </li>
                            <li>
                              <strong>Microsoft Outlook:</strong> Sends critical
                              internal and external communications automatically
                              to the relevant parties as needed.
                            </li>
                            <li>
                              <strong>PeakSuite Document Generator:</strong>{" "}
                              Transforms data and documents into Quickbase
                              formatted assets stored within the platform.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-blue">
                        <div className="client-content">
                          <h2 className="text-white">The Results</h2>
                          <p className="text-white">
                            <Link
                              to="/custom-quickbase-application-development/"
                              className="text-white underline"
                            >
                              The new applications{" "}
                            </Link>{" "}
                            and workflows eliminate excessive manual data entry
                            and record management for the back-office and
                            executive teams. From invoicing to HR to asset
                            management, the team saves 300+ hours a year with
                            automation.
                          </p>
                          <p className="text-white">
                            Previously, the weekly invoicing process took the
                            CEO four days each week to complete. The new
                            application automates these workflows, giving the
                            CEO time to focus on the business.
                          </p>
                          <p className="text-white">
                            The client consolidated their tech stack, exchanging
                            several out-of-the-box solutions for the custom
                            Quickbase CRM and ERP. And without tech bloat, they
                            only get the features needed to run operations more
                            efficiently.
                          </p>

                          <p>
                            <strong className="text-white">Key Results:</strong>
                          </p>
                          <ol className="decimal text-white">
                            <li className="text-white">
                              Saved 300+ hours per year by automating
                              back-office workflows
                            </li>
                            <li className="text-white">
                              Cut weekly invoicing from four days to minutes
                            </li>
                            <li className="text-white">
                              Consolidated multiple tools into a single,
                              field-ready ERP
                            </li>
                            <li className="text-white">
                              Improved job tracking and billing accuracy across
                              all client work
                            </li>
                            <li className="text-white">
                              Freed up executive time to focus on service
                              delivery and growth
                            </li>
                          </ol>
                          <blockquote
                            className="testimonial"
                            style={{
                              background: "#fff",
                              borderRadius: "24px",
                              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                              padding: "32px 24px 16px 24px",
                              maxWidth: "100%",
                              textAlign: "center",
                              position: "relative",
                            }}
                          >
                            <div className="row mb-3">
                              <div className="col-lg-8">
                                <p
                                  style={{
                                    color: "#3a3a3a",
                                    fontStyle: "italic",
                                    fontSize: "20px",
                                  }}
                                >
                                  "If we were to grow four times our current
                                  size, it wouldn’t be an issue. It’s an amazing
                                  system, and we’re thrilled with how it’s
                                  turned out."
                                </p>
                              </div>
                              <div className="col-lg-4 mt-4 mt-lg-0">
                                <div className="row">
                                  <div className="col-6 col-md-12">
                                    <div
                                      className="relative user-img mb-2"
                                      style={{
                                        backgroundImage:
                                          'url("https://cdn.sanity.io/images/55lcecww/production/56ccd6002fe6b279fa4421266a8893aaa69aad13-200x200.jpg")',
                                      }}
                                    />
                                  </div>
                                  <div className="col-6 col-md-12">
                                    <div className="relative text-left user-info ml-2">
                                      <h3>Bob Rizzuto</h3>
                                      <p className="my-2 my-md-0">
                                        President Advanced Fiber
                                      </p>
                                      <a
                                        href="https://www.linkedin.com/in/bob-rizzuto-b7076135/"
                                        target="_blank"
                                      >
                                        <svg
                                          fill="#00b5f4 "
                                          stroke-width="0"
                                          viewBox="0 0 448 512"
                                          height="2em"
                                          width="2em"
                                          xmlns="http://www.w3.org/2000/svg"
                                          style={{
                                            height: "2em; width: 1.2em",
                                          }}
                                        >
                                          <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
                                        </svg>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </blockquote>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>What’s Next?</h2>
                          <p>
                            AFI continues to expand the platform as new
                            operational needs emerge. Quandary remains engaged{" "}
                            {""} <Link to="/faq">to support that growth</Link>,
                            delivering new modules and improvements regularly.
                          </p>
                          <p>
                            Recent initiatives include building a pricing
                            management layer to track material costs/margins and
                            exploring new scheduling logic to better balance
                            on-call shifts across field teams.
                          </p>
                          <p>
                            With each iteration, the platform becomes more
                            central to th team’s operations. It reduces
                            overhead, supports compliance, and enables smarter
                            decisions across departments.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Client Overview:</h2>
                          <p>
                            Advanced Fiber (AFI) is a New York-based field
                            services provider supporting broadband and fiber
                            infrastructure projects across the Northeast. They
                            specialize in splicing, testing, documentation, and
                            compliance for major telecommunications clients.
                          </p>

                          <div className="row align-items-center">
                            <div className="col-md-6 pr-md-4">
                              <p>
                                <strong>Industry:</strong> Telecommunications
                                Field Services
                                <span className="relative d-block">
                                  <strong>Location:</strong> New York, with
                                  operations in CT, NJ, and PA
                                </span>
                                <span className="relative d-block">
                                  <strong>Size:</strong> 50–100 employees
                                </span>
                              </p>
                            </div>
                            <div className="col-md-6 pl-md-4 d-flex justify-content-center align-items-center border-left-md">
                              <LogoGrid>
                                <LogoCard>
                                  <LogoImg
                                    src="https://cdn.sanity.io/images/55lcecww/production/bd96b7fdc6b9e484cda308373b077bd863a5e4ec-800x200.jpg"
                                    alt="Advanced Fiber"
                                  />
                                </LogoCard>
                              </LogoGrid>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto px-0 pt-4 content-wrapper">
                        <div className="client-content">
                          <div id="speak-with-specialist">
                            <div className="form-wrap-contact">
                              <div className="form-wrapper">
                                <div className="intro mb-2">
                                  <div className="row">
                                    <div className="col-md-12">
                                      <h2 className="mt-0 font-weight-medium">
                                        Speak with Our Innovation Team
                                      </h2>
                                      <p>
                                        Use the form below to drop us an e-mail.
                                        We'll be in touch shortly.
                                      </p>
                                      <small className="text-red">
                                        Fields marked with an asterisk (*) are
                                        required.
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                <WebriQForm
                                  id="contact-form"
                                  name="Contact Form"
                                  className="contactForm"
                                  data-form-id="4d46fe65-4d66-46b4-946c-e5826349ed3c"
                                  data-thankyou-url="/thank-you"
                                >
                                  <div className="row">
                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                      {/* <p class="required">Full Name</p> */}

                                      <input
                                        type="text"
                                        name="Full Name"
                                        size={40}
                                        className="form-control required"
                                        placeholder="Full Name *"
                                        aria-label="Full Name"
                                        required
                                        id="requiredField"
                                      />
                                    </div>
                                    <div className="col-lg-6 col-md-12 col-sm-12 mt-4 mt-lg-0">
                                      {/* <p class="required">Phone</p> */}
                                      <input
                                        type="text"
                                        name="Phone"
                                        size={40}
                                        // minLength={14}
                                        className="form-control required"
                                        id="phone"
                                        placeholder="Phone Number *"
                                        aria-label="Phone"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Email</p> */}
                                      <input
                                        type="email"
                                        name="Email"
                                        size={40}
                                        className="form-control"
                                        aria-label="Email"
                                        placeholder="Email *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Message</p> */}
                                      <textarea
                                        name="Message"
                                        rows={5}
                                        cols={0}
                                        className="form-control"
                                        aria-label="Message"
                                        placeholder="Message *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">How did you find out about us?</p> */}
                                      <div>
                                        <textarea
                                          type="text"
                                          name="How did you find out about us?"
                                          size={40}
                                          rows={5}
                                          className="form-control"
                                          aria-label="How did you find out about us?"
                                          placeholder="How did you find out about us? *"
                                          required
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="form-group col-12 d-flex justify-content-start captcha-wrap">
                                      <div className="webriq-recaptcha" />
                                    </div>
                                    <div className="form-group col-12 text-left">
                                      <div className="input-filled">
                                        <button
                                          type="submit"
                                          className="btn btn-primary btn-arrow"
                                        >
                                          SUBMIT
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </WebriQForm>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}

export default AFI
